import React, { useState, useEffect, useRef } from 'react';
import {
  PlusIcon,
  CheckIcon,
  HeartIcon,
  PhotoIcon,
  HashtagIcon,
} from '@heroicons/react/24/outline';
import AttributeLabel from './AttributeLabel';
import { ModalShell, ModalActions, ModalSection } from './modals/core';

interface Dog {
  dog_id: number;
  hundename: string;
  vollstaendiger_name?: string;
  rasse?: string;
  geburtstag?: string;
  chipnummer?: string;
  tassonummer?: string;
  tollwut_end_date?: string;
  geschlecht?: string;
  kastriert?: boolean;
  hundebox?: string;
  insurance_name?: string;
  valid_tollwut: boolean;
  valid_insurance: boolean;
  tollwut_checked_at?: string;
  insurance_checked_at?: string;
  last_audit_insurance?: string;
  last_audit_tollwut?: string;
  profile_image?: string;
  image_path?: string; // Add this field from API response
  image_path_updated?: string; // Timestamp for cache busting
}

interface AddDogModalProps {
  isOpen: boolean;
  onClose: () => void;
  memberId: string;
  onSubmit: (formData: FormData) => Promise<void>;
}

interface EditDogModalProps {
  isOpen: boolean;
  onClose: () => void;
  dog: Dog;
  onSubmit: (formData: FormData, dogId: number) => Promise<void>;
}

export const AddDogModal: React.FC<AddDogModalProps> = ({ isOpen, onClose, memberId, onSubmit }) => {
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const formRef = useRef<HTMLFormElement>(null);

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e?: React.FormEvent<HTMLFormElement>) => {
    e?.preventDefault();
    if (!formRef.current) return;
    const formData = new FormData(formRef.current);
    await onSubmit(formData);
  };

  return (
    <ModalShell
      open={isOpen}
      onClose={() => onClose()}
      variant="form"
      size="lg"
      icon={HeartIcon}
      title="Hund hinzufügen"
      closeOnBackdrop={false}
      footer={
        <ModalActions
          secondary={{ label: 'Abbrechen', onClick: onClose }}
          primary={{
            label: 'Hinzufügen',
            onClick: () => handleSubmit(),
            icon: <PlusIcon className="h-5 w-5" />,
          }}
        />
      }
    >
      <form ref={formRef} onSubmit={handleSubmit}>
        {/* Profilbild */}
        <div className="mb-6 flex justify-center">
          <div className="relative">
            {imagePreview ? (
              <img
                src={imagePreview}
                alt="Hund Profilbild"
                className="w-32 h-32 rounded-full object-cover border-4 border-gray-200 dark:border-gray-600"
              />
            ) : (
              <div className="w-32 h-32 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center">
                <PhotoIcon className="h-12 w-12 text-gray-400" />
              </div>
            )}
            <label className="absolute bottom-0 right-0 bg-primary-600 hover:bg-primary-700 text-white p-2 rounded-full cursor-pointer transition-colors">
              <PhotoIcon className="h-5 w-5" />
              <input
                type="file"
                name="image"
                accept="image/*"
                onChange={handleImageChange}
                className="hidden"
              />
            </label>
          </div>
        </div>

        <div className="space-y-6">
          {/* Basisdaten */}
          <ModalSection title="Basisdaten">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <AttributeLabel iconKey="name" className="block mb-1" textClassName="text-sm font-medium text-gray-700 dark:text-gray-300">Rufname *</AttributeLabel>
                <input
                  type="text"
                  name="rufname"
                  required
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
                />
              </div>

              <div>
                <AttributeLabel iconKey="name" className="block mb-1" textClassName="text-sm font-medium text-gray-700 dark:text-gray-300">Vollständiger Name</AttributeLabel>
                <input
                  type="text"
                  name="vollstaendiger_name"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
                />
              </div>

              <div>
                <AttributeLabel iconKey="breed" className="block mb-1" textClassName="text-sm font-medium text-gray-700 dark:text-gray-300">Rasse</AttributeLabel>
                <input
                  type="text"
                  name="rasse"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
                />
              </div>

              <div>
                <AttributeLabel iconKey="birthday" className="block mb-1" textClassName="text-sm font-medium text-gray-700 dark:text-gray-300">Geburtstag</AttributeLabel>
                <input
                  type="date"
                  name="geburtstag"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
                />
              </div>

              <div>
                <AttributeLabel iconKey="gender" className="block mb-1" textClassName="text-sm font-medium text-gray-700 dark:text-gray-300">Geschlecht</AttributeLabel>
                <select
                  name="geschlecht"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
                >
                  <option value="">Bitte wählen</option>
                  <option value="Rüde">Rüde</option>
                  <option value="Hündin">Hündin</option>
                </select>
              </div>

              <div>
                <AttributeLabel iconKey="neutered" className="block mb-1" textClassName="text-sm font-medium text-gray-700 dark:text-gray-300">Kastriert</AttributeLabel>
                <select
                  name="kastriert"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
                >
                  <option value="Nein">Nein</option>
                  <option value="Ja">Ja</option>
                </select>
              </div>
            </div>
          </ModalSection>

          {/* Registrierung */}
          <ModalSection title="Registrierung">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <AttributeLabel iconKey="chipNumber" className="block mb-1" textClassName="text-sm font-medium text-gray-700 dark:text-gray-300">Chipnummer</AttributeLabel>
                <input
                  type="text"
                  name="chipnummer"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  <div className="flex items-center gap-2">
                    <HashtagIcon className="h-4 w-4 text-primary-600" />
                    Tasso-Nummer
                  </div>
                </label>
                <input
                  type="text"
                  name="tassonummer"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
                />
              </div>
            </div>
          </ModalSection>

          {/* Gesundheit & Extras */}
          <ModalSection title="Gesundheit & Extras">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <AttributeLabel iconKey="vaccination" className="block mb-1" textClassName="text-sm font-medium text-gray-700 dark:text-gray-300">Tollwut gültig bis</AttributeLabel>
                <input
                  type="date"
                  name="impfstatus_tollwut"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
                />
              </div>

              <div>
                <AttributeLabel iconKey="liability" className="block mb-1" textClassName="text-sm font-medium text-gray-700 dark:text-gray-300">Versicherung</AttributeLabel>
                <input
                  type="text"
                  name="insurance_name"
                  placeholder="Name der Versicherung"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
                />
              </div>

              <div>
                <AttributeLabel iconKey="dogBox" className="block mb-1" textClassName="text-sm font-medium text-gray-700 dark:text-gray-300">Hundebox</AttributeLabel>
                <select
                  name="hundebox"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
                >
                  <option value="">Bitte wählen</option>
                  <option value="Ja">Ja</option>
                  <option value="Nein">Nein</option>
                </select>
              </div>
            </div>
          </ModalSection>
        </div>
      </form>
    </ModalShell>
  );
};

export const EditDogModal: React.FC<EditDogModalProps> = ({ isOpen, onClose, dog, onSubmit }) => {
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [tollwutChecked, setTollwutChecked] = useState(false);
  const [insuranceChecked, setInsuranceChecked] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);

  // Set initial image when dog changes or modal opens
  useEffect(() => {
    // If dog has an image_path, construct the proper API URL
    if (dog.image_path && dog.dog_id) {
      // Get JWT token from localStorage
      const token = localStorage.getItem('jwt_token');
      const timestamp = dog.image_path_updated || new Date().getTime();

      // Construct the image URL using the API endpoint format
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:4000';
      const imageUrl = `${apiUrl}/api/dogs/image-url/${dog.dog_id}?size=M&token=${token}&t=${timestamp}`;

      setImagePreview(imageUrl);
    } else if (dog.profile_image) {
      // Fallback to profile_image if available
      setImagePreview(dog.profile_image);
    } else {
      setImagePreview(null);
    }
  }, [dog]);

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e?: React.FormEvent<HTMLFormElement>) => {
    e?.preventDefault();
    if (!formRef.current) return;
    const formData = new FormData(formRef.current);

    // Add audit checkboxes
    if (tollwutChecked) {
      formData.append('tollwut_checked', 'true');
    }
    if (insuranceChecked) {
      formData.append('insurance_checked', 'true');
    }

    await onSubmit(formData, dog.dog_id);
  };

  return (
    <ModalShell
      open={isOpen}
      onClose={() => onClose()}
      variant="form"
      size="lg"
      icon={HeartIcon}
      title="Hund bearbeiten"
      closeOnBackdrop={false}
      footer={
        <ModalActions
          secondary={{ label: 'Abbrechen', onClick: onClose }}
          primary={{
            label: 'Speichern',
            onClick: () => handleSubmit(),
            icon: <CheckIcon className="h-5 w-5" />,
          }}
        />
      }
    >
      <form ref={formRef} onSubmit={handleSubmit}>
        {/* Profilbild */}
        <div className="mb-6 flex justify-center">
          <div className="relative">
            {imagePreview ? (
              <img
                src={imagePreview}
                alt="Hund Profilbild"
                className="w-32 h-32 rounded-full object-cover border-4 border-gray-200 dark:border-gray-600"
              />
            ) : (
              <div className="w-32 h-32 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center">
                <PhotoIcon className="h-12 w-12 text-gray-400" />
              </div>
            )}
            <label className="absolute bottom-0 right-0 bg-primary-600 hover:bg-primary-700 text-white p-2 rounded-full cursor-pointer transition-colors">
              <PhotoIcon className="h-5 w-5" />
              <input
                type="file"
                name="image"
                accept="image/*"
                onChange={handleImageChange}
                className="hidden"
              />
            </label>
          </div>
        </div>

        <div className="space-y-6">
          {/* Basisdaten */}
          <ModalSection title="Basisdaten">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <AttributeLabel iconKey="name" className="block mb-1" textClassName="text-sm font-medium text-gray-700 dark:text-gray-300">Rufname *</AttributeLabel>
                <input
                  type="text"
                  name="rufname"
                  defaultValue={dog.hundename}
                  required
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
                />
              </div>

              <div>
                <AttributeLabel iconKey="name" className="block mb-1" textClassName="text-sm font-medium text-gray-700 dark:text-gray-300">Vollständiger Name</AttributeLabel>
                <input
                  type="text"
                  name="vollstaendiger_name"
                  defaultValue={dog.vollstaendiger_name}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
                />
              </div>

              <div>
                <AttributeLabel iconKey="breed" className="block mb-1" textClassName="text-sm font-medium text-gray-700 dark:text-gray-300">Rasse</AttributeLabel>
                <input
                  type="text"
                  name="rasse"
                  defaultValue={dog.rasse}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
                />
              </div>

              <div>
                <AttributeLabel iconKey="birthday" className="block mb-1" textClassName="text-sm font-medium text-gray-700 dark:text-gray-300">Geburtstag</AttributeLabel>
                <input
                  type="date"
                  name="geburtstag"
                  defaultValue={dog.geburtstag?.split('T')[0]}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
                />
              </div>

              <div>
                <AttributeLabel iconKey="gender" className="block mb-1" textClassName="text-sm font-medium text-gray-700 dark:text-gray-300">Geschlecht</AttributeLabel>
                <select
                  name="geschlecht"
                  defaultValue={dog.geschlecht}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
                >
                  <option value="">Bitte wählen</option>
                  <option value="Rüde">Rüde</option>
                  <option value="Hündin">Hündin</option>
                </select>
              </div>

              <div>
                <AttributeLabel iconKey="neutered" className="block mb-1" textClassName="text-sm font-medium text-gray-700 dark:text-gray-300">Kastriert</AttributeLabel>
                <select
                  name="kastriert"
                  defaultValue={dog.kastriert ? 'Ja' : 'Nein'}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
                >
                  <option value="Nein">Nein</option>
                  <option value="Ja">Ja</option>
                </select>
              </div>
            </div>
          </ModalSection>

          {/* Registrierung */}
          <ModalSection title="Registrierung">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <AttributeLabel iconKey="chipNumber" className="block mb-1" textClassName="text-sm font-medium text-gray-700 dark:text-gray-300">Chipnummer</AttributeLabel>
                <input
                  type="text"
                  name="chipnummer"
                  defaultValue={dog.chipnummer}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  <div className="flex items-center gap-2">
                    <HashtagIcon className="h-4 w-4 text-primary-600" />
                    Tasso-Nummer
                  </div>
                </label>
                <input
                  type="text"
                  name="tassonummer"
                  defaultValue={dog.tassonummer}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
                />
              </div>
            </div>
          </ModalSection>

          {/* Gesundheit & Extras */}
          <ModalSection title="Gesundheit & Extras">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <AttributeLabel iconKey="vaccination" className="block mb-1" textClassName="text-sm font-medium text-gray-700 dark:text-gray-300">Tollwut gültig bis</AttributeLabel>
                <input
                  type="date"
                  name="impfstatus_tollwut"
                  defaultValue={dog.tollwut_end_date?.split('T')[0]}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
                />
              </div>

              <div>
                <AttributeLabel iconKey="liability" className="block mb-1" textClassName="text-sm font-medium text-gray-700 dark:text-gray-300">Versicherung</AttributeLabel>
                <input
                  type="text"
                  name="insurance_name"
                  defaultValue={dog.insurance_name}
                  placeholder="Name der Versicherung"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
                />
              </div>

              <div>
                <AttributeLabel iconKey="dogBox" className="block mb-1" textClassName="text-sm font-medium text-gray-700 dark:text-gray-300">Hundebox</AttributeLabel>
                <select
                  name="hundebox"
                  defaultValue={dog.hundebox || ''}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
                >
                  <option value="">Bitte wählen</option>
                  <option value="Ja">Ja</option>
                  <option value="Nein">Nein</option>
                </select>
              </div>
            </div>
          </ModalSection>

          {/* Audit Section */}
          <ModalSection title="Audit">
            <div className="space-y-3">
              <label className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600">
                <input
                  type="checkbox"
                  checked={tollwutChecked}
                  onChange={(e) => setTollwutChecked(e.target.checked)}
                  className="w-5 h-5 text-primary-600 border-gray-300 dark:border-gray-600 rounded focus:ring-primary-500"
                />
                <span className="text-sm text-gray-700 dark:text-gray-300">
                  Tollwutimpfung wurde geprüft
                </span>
                {dog.last_audit_tollwut && (
                  <span className="text-xs text-gray-500 ml-auto">
                    Letzte Prüfung: {new Date(dog.last_audit_tollwut).toLocaleDateString('de-DE')}
                  </span>
                )}
              </label>

              <label className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600">
                <input
                  type="checkbox"
                  checked={insuranceChecked}
                  onChange={(e) => setInsuranceChecked(e.target.checked)}
                  className="w-5 h-5 text-primary-600 border-gray-300 dark:border-gray-600 rounded focus:ring-primary-500"
                />
                <span className="text-sm text-gray-700 dark:text-gray-300">
                  Versicherung wurde geprüft
                </span>
                {dog.last_audit_insurance && (
                  <span className="text-xs text-gray-500 ml-auto">
                    Letzte Prüfung: {new Date(dog.last_audit_insurance).toLocaleDateString('de-DE')}
                  </span>
                )}
              </label>
            </div>
          </ModalSection>
        </div>
      </form>
    </ModalShell>
  );
};
